import { supabase } from '@/integrations/supabase/client';
import { getCurrentBucketName } from '@/lib/services/storage-service';

/**
 * Utility function to get image URL from Supabase storage
 */
export async function getImageUrl(path: string, fallbackUrl?: string): Promise<string> {
  try {
    // Get the bucket name from the storage configuration
    const bucketName = getCurrentBucketName();
    
    const { data } = await supabase.storage
      .from(bucketName)
      .getPublicUrl(path);
      
    if (!data) {
      throw new Error('No data returned from storage');
    }
    
    // Check if the URL actually points to an existing image
    const response = await fetch(data.publicUrl, { method: 'HEAD' });
    if (!response.ok) {
      throw new Error(`Image not found: ${response.status}`);
    }
    
    return data.publicUrl;
  } catch (error) {
    console.warn(`Couldn't fetch image from Supabase: ${path}`, error);
    // Return the fallback URL or a placeholder
    return fallbackUrl || 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=80&w=500&auto=format&fit=crop';
  }
}

/**
 * Get common search terms based on saved suggestions in the database
 */
export async function getCommonSearchTerms(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('search_suggestions')
      .select('suggestion')
      .order('click_count', { ascending: false })
      .limit(10);
      
    if (error) {
      console.error('Error fetching common search terms:', error);
      return [];
    }
    
    return data.map(item => item.suggestion);
  } catch (error) {
    console.error('Error in getCommonSearchTerms:', error);
    return [
      'Kedarkantha',
      'Valley of Flowers',
      'Hampta Pass',
      'Nandi Hills',
      'Uttari Betta'
    ];
  }
}

/**
 * Get search suggestions based on query string from the database
 */
export async function getSearchSuggestions(query: string): Promise<string[]> {
  if (!query || query.trim().length < 2) {
    return [];
  }
  
  try {
    // First try searching with pg_trgm for fuzzy matching
    const { data, error } = await supabase
      .from('search_suggestions')
      .select('suggestion, click_count')
      .ilike('suggestion', `%${query}%`)
      .order('is_promoted', { ascending: false })
      .order('click_count', { ascending: false })
      .limit(8);
      
    if (error) {
      console.error('Error fetching search suggestions:', error);
      return [];
    }
    
    if (data.length > 0) {
      return data.map(item => item.suggestion);
    }
    
    // If no results, check trek names
    const { data: trekData, error: trekError } = await supabase
      .from('treks')
      .select('name')
      .ilike('name', `%${query}%`)
      .limit(5);
      
    if (trekError) {
      console.error('Error fetching trek suggestions:', trekError);
      return [];
    }
    
    return trekData.map(trek => trek.name);
  } catch (error) {
    console.error('Error in getSearchSuggestions:', error);
    
    // Fallback to local suggestions in case of errors
    const fallbackSuggestions = [
      'Himalayan Treks',
      'Karnataka Treks',
      'Uttarakhand Treks',
      'Winter Treks',
      'Weekend Treks'
    ];
    
    return fallbackSuggestions.filter(suggestion => 
      suggestion.toLowerCase().includes(query.toLowerCase()));
  }
}

/**
 * Record that a search suggestion was clicked
 */
export async function recordSuggestionClick(suggestion: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('record_suggestion_click', {
      suggestion_text: suggestion
    });
    
    if (error) {
      console.error('Error recording suggestion click:', error);
    }
  } catch (error) {
    console.error('Error in recordSuggestionClick:', error);
  }
}
